import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {ExpansionProps} from '../types';
import {
  getTargetElementOrInstance,
  nativeIdPathsEqual,
  slotElementHasDefaults,
} from '@packages/utilities/platform';
import {isSlot} from '@packages/utilities/elementTypeUtils';
import {namesEqual} from '@packages/systems/wfdl-Qualified';

export function addSlotOverlay<T extends ExpansionProps>(
  props: T,
  runtimeEnvironment: Environment
): T {
  const {
    classes,
    nativeIdPath,
    environmentContext,
    element,
    components,
    plugins,
  } = props;
  if (!isSlot(element) || runtimeEnvironment !== WebflowEnvironments.DESIGNER) {
    return props;
  }
  const targetElementOrInstance = getTargetElementOrInstance(
    nativeIdPath,
    components
  );
  // `focusedComponentType` is a `Qualified` tuple; guard it before `namesEqual`,
  // which indexes `[0]`/`[1]` and throws on undefined. It's absent whenever no
  // component is focused (e.g. viewing the page), so without this guard any slot
  // rendered on the canvas in that state crashes the renderer.
  const focusedComponentType = environmentContext?.get('focusedComponentType');
  const focusedSymbolNativeIdPath = environmentContext?.get(
    'focusedSymbolNativeIdPath'
  );
  if (
    targetElementOrInstance?.type &&
    focusedComponentType &&
    namesEqual(targetElementOrInstance.type, focusedComponentType) &&
    focusedSymbolNativeIdPath &&
    nativeIdPathsEqual(nativeIdPath, focusedSymbolNativeIdPath)
  ) {
    // While the definition is being edited, the slot renders its default content,
    // so the empty-slot overlay (wf-slot) would draw on top of it. Skip the overlay
    // when the slot has defaults.
    if (
      slotElementHasDefaults(
        plugins,
        components,
        targetElementOrInstance.type,
        element
      )
    ) {
      return props;
    }
    const updatedClasses = classes.push('wf-slot');
    return {...props, classes: updatedClasses};
  }
  return props;
}
